import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useMyTeamStore } from '../../store/my-team.store'
import { PageMetrics } from '../molecules/PageMetrics'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { useSkillSelection } from '../../hook/useSkillSelection'
import { useRatingFilter } from '../../hook/useRatingFilter'
import { SkillSelector } from '../molecules/SkillSelector'
import { RatingFilter } from '../molecules/RatingFilter'
import { InsightsTable } from '../molecules/InsightsTable'
import { buildMetricsConfig } from '../../util/skillGapUtils'

export type SkillGapInsightsProps = {
  skillName?: string
}

/**
 * Skill Gap Insights component for selecting and analyzing team skills.
 * Features multi-select autocomplete with server-side search, pagination, and overlapping skill detection.
 */
export const SkillGapInsights = observer(({ skillName }: SkillGapInsightsProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  
  const { selectedSkills, setSelectedSkills, allSkills, totalCount, hasMore, inputValue, isLoadingSkills, isInitialLoad, handleLoadMore, handleInputChange } = useSkillSelection(skillName)
  const { minRating, maxRating, handleRatingClick, isRatingSelected, resetRating } = useRatingFilter()
  const { isLoading: isLoadingInsights } = myTeamStore.getGenericFieldsState('getSkillGapInsights')
  const insightsData = myTeamStore.skillGapInsights
  
  const { skillInsights, userInsights } = useMemo(() => buildMetricsConfig(insightsData), [insightsData, translation])

  const handleFetchInsights = () => {
    const skillIds = selectedSkills.flatMap(skill => skill.ids)
    myTeamStore.getSkillGapInsights(skillIds, minRating, maxRating)
  }

  const handleReset = () => {
    setSelectedSkills([])
    resetRating()
    myTeamStore.skillGapInsights = undefined
  }

  useEffect(() => {
    if (insightsData && selectedSkills.length === 0) {
      myTeamStore.skillGapInsights = undefined
    }
  }, [selectedSkills, insightsData, myTeamStore, translation])

  return (
    <AppBox sx={styles.root}>
      <AppPaper sx={styles.filterPaper}>
        <SkillSelector
          selectedSkills={selectedSkills}
          allSkills={allSkills}
          totalCount={totalCount}
          hasMore={hasMore}
          inputValue={inputValue}
          isLoading={isLoadingSkills}
          isInitialLoad={isInitialLoad}
          onSkillsChange={setSelectedSkills}
          onInputChange={handleInputChange}
          onLoadMore={handleLoadMore}
        />

        <RatingFilter
          minRating={minRating}
          maxRating={maxRating}
          onRatingClick={handleRatingClick}
          isRatingSelected={isRatingSelected}
        />

        <AppBox sx={styles.buttonContainer}>
          <AppButton
            textValue={translation.pages.myTeam.skillGapInsights.fetchInsights()}
            variant="contained"
            onClick={handleFetchInsights}
            disabled={selectedSkills.length === 0 || isLoadingInsights}
            sx={{ width: '100%', fontSize: 14, fontWeight: 500 }}
          />
          <AppButton
            textValue={translation.pages.myTeam.skillGapInsights.reset()}
            variant="light"
            onClick={handleReset}
            disabled={selectedSkills.length === 0 && minRating === null && maxRating === null && !insightsData}
            sx={{ width: '100%', fontSize: 14, fontWeight: 500 }}
          />
        </AppBox>

        <AppBox sx={styles.filterSection}>
          <Caption sx={styles.filterLabel}>{translation.pages.myTeam.skillGapInsights.insightsOverview()}</Caption>
          {/* Skill Insights */}
          <Caption sx={styles.filterLabel}>
            {translation.pages.myTeam.skillGapInsights.skillOverview()}
          </Caption>
          <PageMetrics
            sx={styles.metricsGrid}
            metrics={skillInsights}
            isLoading={isLoadingInsights}
            size="small"
          />
          
          {/* User Insights */}
          <Caption sx={styles.filterLabel}>
            {translation.pages.myTeam.skillGapInsights.userOverview()}
          </Caption>
          <PageMetrics
            sx={styles.metricsGrid}
            metrics={userInsights}
            isLoading={isLoadingInsights}
            size="small"
          />
        </AppBox>
      </AppPaper>

      <AppPaper sx={styles.usersPaper}>
        <InsightsTable insightsData={insightsData} isLoading={isLoadingInsights} />
      </AppPaper>
    </AppBox>
  )
})

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: 10, sm: 12, md: 15 },
    minHeight: { xs: 'auto', md: 'calc(100vh - 100px)' },
    height: { xs: 'auto', md: 'calc(100vh - 100px)' },
  },
  filterPaper: {
    width: { xs: '100%', md: '35%', lg: '30%' },
    minWidth: { xs: 'auto', sm: '300px' },
    height: { xs: 'auto', md: '100%' },
    flexDirection: 'column',
    borderRadius: '10px',
    padding: { xs: '12px', sm: '15px' },
    gap: { xs: 12, md: 15 },
    overflowY: 'auto',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: '100%',
  },
  filterLabel: {
    fontSize: 11,
    color: 'text.secondary',
  },
  usersPaper: {
    flex: 1,
    height: { xs: 'auto', md: '100%' },
    minHeight: { xs: '400px', md: 'auto' },
    flexDirection: 'column',
    borderRadius: '10px',
    padding: { xs: '12px', sm: '15px' },
    gap: { xs: 12, md: 15 },
    overflowY: 'auto',
    display: 'flex',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: { xs: 10, sm: 12, md: 15 },
    width: '100%',
  },
  buttonContainer: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
    gap: { xs: 8, sm: 10 },
    mt: 1,
    width: '100%',
  },
}
